import { Vector2 } from "@babylonjs/core/Maths/math.vector";
import { ERR_INVALID_STATE, newError } from "../../utils/error";
import { BoundingBox } from "../mdl/BoundingBox";
import { CoordinatesType } from "../view/View";
import { SelectionDefault } from "./SelectionDefault";
/**
 * The SelectionMenu can be used to provide a custom menu for the selected SceneElements.
 * A show and hide callback has to be provided to implement the custom menu.
 * The SelectionMenu only works in combination with a default tool derived from the SelectionDefault tool.
 */
var SelectionMenu = /** @class */ (function () {
    /**
     * Creates and registers a new SelectionMenu.
     * @param pApp Reference to the core application.
     * @param pShowCallback Called if the menu should be shown. A 2d bounding rect in viewer coordinates is given for the current selection.
     * @param pHideCallback Called if the menu should be hidden.
     */
    function SelectionMenu(pApp, pShowCallback, pHideCallback) {
        var _this = this;
        this.onElementClicked = function (pElement) {
            if (pElement == null)
                return;
            var tTopLevel = pElement.getTopLevelElement();
            if (tTopLevel.isSelected) {
                _this.setActive(!_this.active);
            }
        };
        this.onToolStarted = function () {
            if (_this.mApp.tools.activeTool === _this.mApp.tools.defaultTool)
                _this.show();
            else {
                if (_this.deactivateOnToolStart)
                    _this.setActive(false);
                else
                    _this.hide();
            }
        };
        this.onInteractorTriggered = function () {
            _this.setActive(false);
        };
        this.onSelectionChanged = function () {
            _this.mActive = true; //force to be active
            _this.show(); //update position / hide if selection is empty
        };
        this.onCameraChanged = function () {
            _this.hide();
            _this.resetCameraTimeout();
            _this.mCameraTimeout = window.setTimeout(function () { return _this.show(); }, 200);
        };
        this.mApp = pApp;
        this.mView = this.mApp.viewer.view;
        this.mVisible = false;
        this.mActive = true;
        this.deactivateOnToolStart = true;
        this.mShowCallback = pShowCallback;
        this.mHideCallback = pHideCallback;
        this.mApp.model.eventSelectionChanged.addListener(this.onSelectionChanged);
        this.mApp.tools.eventToolStarted.addListener(this.onToolStarted);
        this.mApp.tools.eventInteractorTriggered.addListener(this.onInteractorTriggered);
        this.mView.cameraControl.eventCameraChanged.addListener(this.onCameraChanged);
        if (this.mApp.tools.defaultTool == null)
            throw newError(ERR_INVALID_STATE, "no default tool defined");
        if (this.mApp.tools.defaultTool instanceof SelectionDefault) {
            this.mApp.tools.defaultTool.eventElementClick.addListener(this.onElementClicked);
        }
        else
            throw newError(ERR_INVALID_STATE, "default tool not derived from SelectionDefault");
        this.show();
    }
    Object.defineProperty(SelectionMenu.prototype, "active", {
        get: function () {
            return (this.mActive);
        },
        set: function (pValue) {
            this.setActive(pValue);
        },
        enumerable: false,
        configurable: true
    });
    /** Disposes this menu. */
    SelectionMenu.prototype.dispose = function () {
        this.hide();
        this.mApp.model.eventSelectionChanged.removeListener(this.onSelectionChanged);
        this.mApp.tools.eventToolStarted.removeListener(this.onToolStarted);
        this.mApp.tools.eventInteractorTriggered.removeListener(this.onInteractorTriggered);
        if (this.mApp.tools.defaultTool instanceof SelectionDefault) {
            this.mApp.tools.defaultTool.eventElementClick.removeListener(this.onElementClicked);
        }
        this.mView.cameraControl.eventCameraChanged.removeListener(this.onCameraChanged);
    };
    SelectionMenu.prototype.setActive = function (pActive) {
        if (pActive === this.mActive)
            return;
        if (this.mActive) {
            this.mActive = false;
            this.hide();
        }
        else {
            this.mActive = true;
            this.show();
        }
    };
    SelectionMenu.prototype.hide = function () {
        if (this.mVisible) {
            this.mVisible = false;
            this.mHideCallback();
        }
    };
    SelectionMenu.prototype.resetCameraTimeout = function () {
        if (this.mCameraTimeout != null) {
            clearTimeout(this.mCameraTimeout);
            this.mCameraTimeout = undefined;
        }
    };
    SelectionMenu.prototype.show = function () {
        this.resetCameraTimeout();
        if (!this.mActive)
            return;
        if (!this.mApp.tools.isDefaultToolRunning() || this.mApp.model.selection.length === 0) {
            this.hide();
            return;
        }
        var tBoundsTransform;
        var tBounds;
        if (this.mApp.model.selection.length > 1) {
            tBounds = new BoundingBox();
            for (var _i = 0, _a = this.mApp.model.selection; _i < _a.length; _i++) {
                var tElement = _a[_i];
                var tElementBounds = tElement.getSelectionHighlightBoundingBox();
                tElementBounds = tElementBounds.transform(tElement.worldTransform);
                tBounds.expandInPlace(tElementBounds);
            }
            tBoundsTransform = null;
        }
        else {
            tBounds = this.mApp.model.selection[0].getSelectionHighlightBoundingBox();
            tBoundsTransform = this.mApp.model.selection[0].worldTransform;
        }
        if (!tBounds.isValid()) {
            this.hide();
            return;
        }
        var tPoints3d = tBounds.getPoints();
        var tBBox = new BoundingBox();
        for (var _b = 0, tPoints3d_1 = tPoints3d; _b < tPoints3d_1.length; _b++) {
            var tPoint3d = tPoints3d_1[_b];
            var tPoint = this.mView.projectPoint3(tPoint3d, CoordinatesType.Viewer, tBoundsTransform, false);
            if (tPoint != null) {
                tBBox.expandByPointInPlace(tPoint);
            }
        }
        if (tBBox.isValid()) {
            this.mVisible = true;
            this.mShowCallback(new Vector2(tBBox.min.x, tBBox.min.y), new Vector2(tBBox.max.x, tBBox.max.y), this.mView);
        }
        else {
            this.hide();
        }
    };
    return SelectionMenu;
}());
export { SelectionMenu };
